<script lang="ts">
	import Card from '$lib/components/Card.svelte';
	import Icon from '$lib/elements/Icon.svelte';
	import { t } from 'svelte-i18n';
	import type { OperationSummaryModel } from '@app/models/api/OperationSummaryModel';
	import { urls } from '@app/urls';
	import {
		adaptFundingOperationData,
		hasAmountReachedExcludingWiseedAndFinancialInstrumentStocks,
		isGoalAmountReachedFn,
		isMinAmountReachedFn
	} from '@app/utils/operations';
	import CollectProgressIndicator from '../CollectProgressIndicator.svelte';
	import { formatAmountWithCurrency } from '$lib/utils/strings';
	import { OperationCategoryEnum } from '@app/models/api/enums/OperationCategoryEnum.js';
	import type { AdaptedOperationModel, OperationModel } from '@app/models/api/OperationModel';
	import { OperationFinancialInstrumentEnum } from '@app/models/api/enums/OperationFinancialInstrumentEnum';
	import { page } from '$app/stores';
	import Hint from '$lib/elements/Hint.svelte';
	import OperationStatus from '../OperationStatus.svelte';

	export let props: OperationSummaryModel | OperationModel;
	let operation: AdaptedOperationModel = adaptFundingOperationData(props);
</script>

<Card
	sector={operation.category}
	title={operation.projectName}
	mediaUrl={operation.projectShortImageCardURL}
	desc={operation.projectShortDescriptionFr}
	isPremiumOnly={operation.isPremiumOnly}
	isPrivateAccess={operation.isPrivateAccess}
	href={urls.PROJECT.INDEX(operation.id)}
	isLoggedIn={!!$page.data.user}
>
	<div slot="alreadyInvest" class="flex items-center text-primary space-x-2">
		{#if operation.amountSubscribed}
			<Icon name="check" size="small" />
			<span class="font-semibold text-sm"
				>{$t('operation.messages.youHaveAlreadyInvestedXEuros', {
					values: { amount: operation.amountSubscribed }
				})}</span
			>
		{/if}
	</div>
	<div slot="status" class="flex justify-between items-center">
		<OperationStatus props={operation} />
	</div>
	<svelte:fragment slot="progress">
		{#if operation.category !== OperationCategoryEnum.FUNDS && !operation.isOngoingClosing && !operation.isCollectEnded}
			<CollectProgressIndicator operation={props} />
		{/if}
	</svelte:fragment>
	<div slot="body">
		<div class="space-y-2">
			{#if operation?.projectTypeTarget !== 'OPC' && operation.nbOfInvestors && (operation.isCollectRunning || operation.isCollectAlmostEnd)}
				<div class="">
					<span class="font-semibold">{operation.nbOfInvestors}</span>
					{$t('investors', { values: { n: operation.nbOfInvestors } })}
				</div>
			{/if}
			{#if operation.category !== OperationCategoryEnum.FUNDS && !!operation.isOngoingClosing && !operation.isCollectEnded && isMinAmountReachedFn(props)}
				<div class="font-bold flex space-x-2 text-success">
					<Icon name="check" size="small" />
					<span
						>{isGoalAmountReachedFn(props)
							? $t('operation.messages.goalAmountReached')
							: $t('operation.messages.minAmountReached')}</span
					>
				</div>
			{/if}
		</div>
	</div>
	<div slot="footer">
		<div class="grid footer-grid grid-cols-3 gap-3">
			{#if operation.amountNeeded || operation.amountNeededWiseeders || operation.amountNeededPremium || operation.amountReserveInstitutional}
				<div class="flex flex-col">
					<div class="font-semibold tracking-normal">
						{formatAmountWithCurrency(operation.amountNeeded)}
					</div>
					<div class="uppercase text-xs flex items-end gap-2">
						<div>{$t('operation.amountToSeek')}</div>
						{#if operation.amountReachedExcludingWiseed && hasAmountReachedExcludingWiseedAndFinancialInstrumentStocks(props)}
							<Hint
								tooltip={{
									content: $t('operation.amountHint', {
										values: {
											amountWiSEED: formatAmountWithCurrency(
												operation.amountNeeded - operation.amountReachedExcludingWiseed
											),
											amountOutsideWiSEED: formatAmountWithCurrency(
												operation.amountReachedExcludingWiseed
											)
										}
									})
								}}
							/>
						{/if}
					</div>
				</div>
			{/if}
			{#if operation.percentageYieldBond}
				<div class="flex flex-col">
					<div class="font-semibold">
						{operation.percentageYieldBond}%
					</div>
					<div class="uppercase text-xs">{$t('performance')}</div>
				</div>
			{:else}
				<div />
			{/if}
			{#if operation.projectTypeTarget !== 'OPC'}
				<div class="flex flex-col">
					<div class="font-semibold">
						{`${
							operation.outingsHorizon
								? `${operation.outingsHorizon} ${$t('date.months')}`
								: operation.financialInstrument ===
									  OperationFinancialInstrumentEnum.EQUITY_SECURITIES
									? $t('operation.headers.equitySecuritiesOutingsHorizon')
									: $t('operation.headers.sharesOutingsHorizon')
						}`}
					</div>
					<div class="uppercase text-xs">{$t('horizon')}</div>
				</div>
			{/if}
		</div>
	</div>
</Card>

<style lang="postcss">
	.footer-grid {
		& > div:nth-child(3) {
			@apply text-right;
		}
		& > div:first-child {
			@apply text-left;
		}
		& > div:nth-child(2) {
			@apply text-center;
		}
	}
</style>
